import slideUp from '../util/slide-up';
import * as a11y from '../vendor/theme-scripts/theme-a11y';

const settings = {
  elements: {
    html: 'html',
    body: 'body',
    inPageLink: '[data-skip-content]',
    linkesWithOnlyHash: 'a[href="#"]',
    triggerFocusElement: '[data-focus-element]',
    cartDropdown: '#cart-dropdown',
    search: '#search-popdown',
    accordionContent: '.accordion-content',
    tabs: '.tabs',
    accordionDataToggle: 'data-accordion-toggle',
  },
  classes: {
    focus: 'is-focused',
    open: 'is-open',
    accordionToggle: 'accordion-toggle',
    tabLink: 'tab-link',
  },
};

class Accessibility {
  constructor() {
    this.init();
  }

  init() {
    this.settings = settings;
    this.window = window;
    this.document = document;
    this.a11y = a11y;
    this.cart = this.window.cart;

    // DOM Elements
    this.inPageLink = this.document.querySelector(this.settings.elements.inPageLink);
    this.linkesWithOnlyHash = this.document.querySelectorAll(this.settings.elements.linkesWithOnlyHash);
    this.html = this.document.querySelector(this.settings.elements.html);
    this.body = this.document.querySelector(this.settings.elements.body);
    this.cartDropdown = this.document.querySelector(this.settings.elements.cartDropdown);
    this.lastFocused = null;

    // Flags
    this.isFocused = false;

    // A11Y init methods
    this.a11y.focusHash();
    this.a11y.bindInPageLinks();

    // Events
    this.clickEvents();
    this.focusEvents();
    this.focusEventsOff();
    this.closeExpandedElements();
  }

  /**
   * Clicked events accessibility
   *
   * @return  {Void}
   */

  clickEvents() {
    if (this.inPageLink) {
      this.inPageLink.addEventListener('click', (event) => {
        event.preventDefault();
      });
    }

    if (this.linkesWithOnlyHash) {
      this.linkesWithOnlyHash.forEach((item) => {
        item.addEventListener('click', (event) => {
          event.preventDefault();
        });
      });
    }
  }

  /**
   * Focus events
   *
   * @return  {Void}
   */

  focusEvents() {
    this.document.addEventListener('keyup', (event) => {
      if (event.keyCode !== window.theme.keyboardKeys.TAB) {
        return;
      }

      this.body.classList.add(this.settings.classes.focus);
      this.isFocused = true;
    });

    // Expand modals
    this.document.addEventListener('keyup', (event) => {
      if (!this.isFocused) {
        return;
      }

      const target = event.target;
      const pressEnterOrSpace = event.keyCode === window.theme.keyboardKeys.ENTER || event.keyCode === window.theme.keyboardKeys.SPACE;
      const targetElement = target.matches(this.settings.elements.triggerFocusElement) || target.closest(this.settings.elements.triggerFocusElement);
      const isAccordion =
        target.classList.contains(this.settings.classes.accordionToggle) ||
        target.parentNode.classList.contains(this.settings.classes.accordionToggle) ||
        target.hasAttribute(this.settings.elements.accordionDataToggle) ||
        target.parentNode.hasAttribute(this.settings.elements.accordionDataToggle);
      const isTab = target.classList.contains(this.settings.classes.tabLink) || target.parentNode.classList.contains(this.settings.classes.tabLink);

      const isSearchModal =
        target.hasAttribute('data-popdown-toggle') ||
        (target.closest(this.settings.elements.triggerFocusElement) && target.closest(this.settings.elements.triggerFocusElement).hasAttribute('data-popdown-toggle'));

      if (pressEnterOrSpace && targetElement) {
        if (this.lastFocused === null) {
          this.lastFocused = target;
        }

        let container = this.document.querySelector(this.settings.elements.cartDropdown);

        if (isSearchModal) {
          container = this.document.querySelector(this.settings.elements.search);
        }

        if (isAccordion) {
          container = target.nextElementSibling;
          target.click();
        }

        if (isTab) {
          const selector = `.tab-content-${target.getAttribute('data-tab')}`;

          container = this.document.querySelector(selector);
          target.click();
        }

        if (container.querySelector('a, input') && !isAccordion && !isTab) {
          this.a11y.trapFocus(container, {
            elementToFocus: container.querySelector('a:first-child, input:first-child'),
          });
        }
      }
    });

    // Focus addToCart button or quickview button
    this.html.addEventListener('cart:add-item', (event) => {
      this.lastFocused = event.detail.selector;
    });
  }

  /**
   * Focus events off
   *
   * @return  {Void}
   */

  focusEventsOff() {
    this.document.addEventListener('mousedown', () => {
      this.body.classList.remove(this.settings.classes.focus);
      this.isFocused = false;
    });
  }

  /**
   * Close expanded elements with when press escape
   *
   * @return  {Void}
   */

  closeExpandedElements() {
    document.addEventListener('keyup', (event) => {
      if (event.keyCode !== window.theme.keyboardKeys.ESCAPE) {
        return;
      }

      this.a11y.removeTrapFocus();

      if (this.html.classList.contains(this.cart.settings.classes.htmlClasses)) {
        this.cart.toggleCartDropdown();
        this.html.classList.remove(this.cart.settings.classes.htmlClasses);
        this.cartDropdown.classList.remove(this.cart.settings.classes.open);
      }

      const accordionContents = document.querySelectorAll(this.settings.elements.accordionContent);

      if (accordionContents.length) {
        for (let i = 0; i < accordionContents.length; i++) {
          if (accordionContents[i].style.display !== 'block') {
            continue;
          }

          const accordionArrow = accordionContents[i].previousElementSibling;
          accordionArrow.classList.remove(this.settings.classes.open);

          slideUp(accordionContents[i]);
        }
      }

      if (this.lastFocused !== null) {
        setTimeout(() => {
          this.lastFocused.focus();
          this.lastFocused = null;
        }, 600);
      }
    });
  }
}

window.accessibility = new Accessibility();
